import { Decimal } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import { formatDate } from "services/utility/dates";
import {
  extractName,
  formatDateTimeToDate,
  formatDocumentInfo,
  formatNumberLinkField,
  extractReturned,
  formatStatus,
  truncate,
  formatCompanyInfo,
  formatBooleanToYesOrBlank,
} from "services/utility/formatting";
import { formatLinkableItem } from "services/utility/formatting";
import { extractDescription } from "services/utility/formatting";
import { formatQuantityWithUom } from "services/utility/formatting";
import {
  inventoryItemTypeEditStrFunction,
  textTypeEditStrFunction,
  decimalTypeEditStrFunction,
  numberTypeEditStrFunction,
  referenceTypeEditStrFunction,
} from "services/utility/list";
import { parseHTML } from "services/utility/parsing";
import { TYPES } from "services/utility/schema";
import { starColumn } from "services/utility/schema";

import { NO_COLUMNS } from "appConstants";
import { DEFAULT_DECIMALS_UNROUNDED, NO_REF_NUMBER_STRING } from "appConstants";

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    heading: i18n("columns.ReferenceNumber"),
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    linkField: true,
    sortable: true,
  },
  {
    name: "date",
    heading: i18n("columns.Date"),
    formatFunc: formatDateTimeToDate,
    minWidth: "8em",
    defaultSort: true,
    sortDirection: "asc",
    sortable: true,
    default: true,
  },
  {
    name: "customer",
    heading: i18n("columns.Customer"),
    formatFunc: extractName,
    minWidth: "10em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "status",
    fieldName: "closed",
    formatFunc: formatStatus,
    heading: i18n("columns.Status"),
    sortable: true,
    default: true,
  },
  {
    name: "received",
    fieldName: "returnStatus",
    heading: i18n("columns.Received"),
    formatFunc: extractReturned,
    minWidth: "10em",
    sortable: true,
    default: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "documents",
    heading: i18n("columns.Documents"),
    formatFunc: formatDocumentInfo,
    minWidth: "15em",
    default: true,
  },
  {
    name: "salesRep",
    heading: i18n("columns.SalesRep"),
    formatFunc: extractName,
    minWidth: "8em",
    sortable: true,
  },
  {
    name: "expiration",
    heading: i18n("columns.ExpDate"),
    formatFunc: formatDate,
    minWidth: "8em",
    sortable: true,
  },
  {
    name: "shippingMethod",
    heading: i18n("columns.ShipMethod"),
    formatFunc: extractName,
  },
  {
    name: "trackingNumber",
    heading: i18n("columns.TrackingNumber"),
    minWidth: "10em",
  },
  {
    name: "department",
    heading: i18n("columns.Department"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "customerNotes",
    heading: i18n("columns.CustomerNotes"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "billing",
    heading: i18n("columns.CustomerAddress"),
    formatFunc: formatCompanyInfo,
    minWidth: "10em",
    maxWidth: "15em",
  },
  {
    name: "shipping",
    heading: i18n("columns.ShippingAddress"),
    formatFunc: formatCompanyInfo,
    minWidth: "10em",
    maxWidth: "15em",
  },
  {
    name: "channel",
    heading: i18n("columns.Channel"),
    formatFunc: extractName,
  },
  {
    name: "customerMessage",
    heading: i18n("columns.CustomerMessage"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
];

export function getEmptyRecord() {
  return {
    archived: false,
    billing: {
      company: null,
      contact: null,
      phone: null,
      email: null,
      addressName: null,
      addressType: null,
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        city: null,
        stateProvince: null,
        postalCode: null,
        country: null,
      },
    },
    channel: null,
    closed: false,
    comment: null,
    customer: null,
    customerMessage: null,
    customerNotes: null,
    customFields: null,
    date: new Date(),
    dateChanged: false,
    department: null,
    documents: [],
    expiration: null,
    hasSignature: false,
    keys: null,
    lines: [],
    linkedReturns: [],
    number: "",
    originalDate: "",
    returnStatus: null,
    salesRep: null,
    shipping: {
      company: null,
      contact: null,
      phone: null,
      email: null,
      addressName: null,
      addressType: null,
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        city: null,
        stateProvince: null,
        postalCode: null,
        country: null,
      },
    },
    shippingMethod: null,
    starred: 0,
    summaryOnly: false,
    syncToken: 0,
    total: new Decimal(0),
    trackingNumber: null,
    values: null,
  };
}

export const LINE_ITEM_METADATA = {
  item: {
    type: TYPES.reference,
    editStr: inventoryItemTypeEditStrFunction,
    initialValue: { id: null, name: null, description: null },
  },
  description: {
    type: TYPES.text,
    editStr: textTypeEditStrFunction,
  },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: decimalTypeEditStrFunction,
    initialValue: new Decimal(0),
  },
  returned: {
    type: TYPES.decimal,
    placesRight: 5,
    editStr: decimalTypeEditStrFunction,
    initialValue: new Decimal(0),
  },
  weight: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: numberTypeEditStrFunction,
    initialValue: new Decimal(0),
  },
  weightunit: {
    type: TYPES.text,
    editStr: textTypeEditStrFunction,
  },
  volume: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: decimalTypeEditStrFunction,
    initialValue: new Decimal(0),
  },
  volumeunit: {
    type: TYPES.text,
    editStr: textTypeEditStrFunction,
  },
  uom: {
    type: TYPES.reference,
    editStr: referenceTypeEditStrFunction,
  },
  class: {
    type: TYPES.reference,
    editStr: referenceTypeEditStrFunction,
  },
  jobWorkcenter: {
    type: TYPES.reference,
    editStr: referenceTypeEditStrFunction,
  },
  itemDetails: { initialValue: {} },
  relatedRecords: { initialValue: {} },
};

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: [],
  MONEY_FIELDS: [],
  DATE_FIELDS: ["expiration"],
  DATETIME_FIELDS: [],
  NON_UTC_DATETIME_FIELDS: ["date"],
};

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  "template",
  "return",
  "shipUPS",
  "archive",
  "openClose",
  "delete",
  "history",
];
export const BATCH_ACTIONS = [
  ["pdf", "email"],
  "delete",
  "archive",
  "openClose",
];

export const REFERENCE_FIELDS = ["customer"];
export const ITEM_CALCULATED_FIELDS = [NO_COLUMNS];

export const QUICK_VIEW_CONFIG = {
  footerSetting: "rmaFooter",
  addressConfig: [
    { name: "billing", label: i18n("address.BillTo") },
    { name: "shipping", label: i18n("address.ShipTo") },
  ],
  tableConfig: [{ type: "lines" }],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDateTimeToDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.RMANumber"),
    },
  ],
  fields: [
    {
      name: "customerMessage",
      label: i18n("frmLabel.CustomerMessage"),
    },
  ],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
      defaultValue: NO_REF_NUMBER_STRING,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantity",
      formatFunc: formatQuantityWithUom,
      heading: i18n("columns.Quantity"),
      align: "right",
    },
  ],
};
